
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row, Spinner } from "reactstrap";
import { IProject, IPublicStatistics, ProjectState } from "@api/schema";
import BaseLayout from "@components/BaseLayout";
import { useSdgs } from "@components/common/contentmanagement/sdgHelper";
import PageError from "@components/common/PageError";
import { withAuthOnLoggedInOnlySetting } from "@components/hoc/withAuthOnLoggedInOnlySetting";
import MarketFilterTabs, { MarketTabItems } from "@components/marketplace/MarketFilterTabs";
import { MarketInfoCardType } from "@components/marketplace/MarketInfoCard";
import MarketSearchForm from "@components/marketplace/MarketSearchForm";
import ProjectScroller from "@components/marketplace/projects/ProjectScroller";
import { loadStatisticsAction } from "@redux/helper/actions";
import { IStatisticsSingleState } from "@redux/helper/reducers";
import { AppState } from "@redux/reducer";
import { StatisticsType } from "@redux/reduxTypes";
import { IMarketSearchParams, MarketProjectFilterType, loadMarketplaceProjectsUsecaseAction, selectLoadedMarketplaceProjectsUsecaseState, setMarketSearchParamsAction } from "@redux/usecases/marketplace";
import { useCategories } from "@services/hooks/useCategories";
import { useCurrentUser } from "@services/hooks/useCurrentUser";
import { getIntFromQuery } from "@services/routes";
import { idExistsInEntities } from "@services/util";
/**
 * this page shows the public project marketplace
 */
const MarketPage: React.FC = () => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();
    // get category and SDG from a possible url-parameter
    const router = useRouter();
    const initialCategoryId = getIntFromQuery(router, "category");
    const initialSDGId = getIntFromQuery(router, "sdg");
    const { categories, categoryRequest } = useCategories();
    const { sdgs, sdgRequest } = useSdgs();
    const publicStatisticsState = useSelector((state: AppState) => state.statistics[StatisticsType.Platform] as IStatisticsSingleState<IPublicStatistics>);
    const projectsState = useSelector((state: AppState) => selectLoadedMarketplaceProjectsUsecaseState(state));
    const marketSearchState = useSelector((state: AppState) => state.marketplaceSearchState);
    const { currentUser } = useCurrentUser();
    // aliases for recurring calls
    const dispatchSetMarketSearchParamsAction = (params: IMarketSearchParams) => dispatch(setMarketSearchParamsAction(params));
    const dispatchLoadMarketProjectsAction = (searchState: IMarketSearchParams) => {
        // work with copy to avoid
        // Error: Invariant failed: A state mutation was detected between dispatches, in the path
        // 'marketplaceSearchState.categories'.  This may cause incorrect behavior.
        // (https://redux.js.org/style-guide/style-guide#do-not-mutate-state)
        const newSearchState = { ...searchState };
        // do not filter for categories and sdgs when filtering for ideas
        if (searchState.projectTypeFilter.includes(MarketProjectFilterType.Ideas)) {
            newSearchState.categories = [];
            newSearchState.sdgs = [];
        }
        dispatch(loadMarketplaceProjectsUsecaseAction(newSearchState));
    };
    /**
     * @returns the current marketSearchState.projectTypeFilter reduced by Ideas
     */
    const currentProjectTypeFilterReducedByIdeas = (): MarketProjectFilterType[] => {
        const newProjectTypeFilter: MarketProjectFilterType[] = marketSearchState.projectTypeFilter || [];
        // "repair": if no projectTypeFilter is set, set all Project-Filter-Types
        if (newProjectTypeFilter.length === 0) {
            newProjectTypeFilter.push(...[MarketProjectFilterType.ProjectsFinished, MarketProjectFilterType.ProjectsInPlanning, MarketProjectFilterType.ProjectsRunning]);
        }
        if (newProjectTypeFilter.includes(MarketProjectFilterType.Ideas)) {
            newProjectTypeFilter.slice(newProjectTypeFilter.indexOf(MarketProjectFilterType.Ideas), 1);
        }
        return newProjectTypeFilter;
    };
    // #region useEffects
    useEffect(() => {
        // if no statistics are given, load them
        if (!publicStatisticsState?.loaded && !publicStatisticsState?.isLoading) {
            dispatch(loadStatisticsAction(StatisticsType.Platform));
        }
        // load on clear state
        // and reload after cleared state after logout
        // NOTE: we don’t know if it’s necessary to trigger loading all data when one of the given state properties changes,
        // but experience showed that this projects/index page may have some special issues
    }, [publicStatisticsState?.loaded]);
    useEffect(() => {
        // if initialCategoryId is given and valid and not already set, set it as filter
        if (idExistsInEntities(initialCategoryId, categories) && marketSearchState.categories?.toString() !== initialCategoryId.toString()) {
            // exclude ideas from projectTypeFilter, because they dont have Categories
            const newProjectTypeFilter = currentProjectTypeFilterReducedByIdeas();
            const params: IMarketSearchParams = {
                categories: [initialCategoryId],
                sdgs: null,
                pattern: null,
                sortBy: marketSearchState.sortBy,
                sortOrder: marketSearchState.sortOrder,
                projectTypeFilter: newProjectTypeFilter,
            };
            dispatchSetMarketSearchParamsAction(params);
        }
        // if initialSDGId is given and valid  and not already set, set it as filter
        if (idExistsInEntities(initialSDGId, sdgs) && marketSearchState.sdgs?.toString() !== initialSDGId.toString()) {
            // exclude ideas from projectTypeFilter, because they dont have SDGs
            const newProjectTypeFilter = currentProjectTypeFilterReducedByIdeas();
            const params: IMarketSearchParams = {
                categories: null,
                sdgs: [initialSDGId],
                pattern: null,
                sortBy: marketSearchState.sortBy,
                sortOrder: marketSearchState.sortOrder,
                projectTypeFilter: newProjectTypeFilter
            };
            dispatchSetMarketSearchParamsAction(params);
        }
    }, [initialCategoryId, initialSDGId, categoryRequest?.loaded, sdgRequest?.loaded]);
    useEffect(() => {
        // set the search params to "ideas only", if there are no active projects at the platform at all
        if (publicStatisticsState?.data?.activeProjects === 0) {
            dispatchSetMarketSearchParamsAction({
                ...marketSearchState,
                projectTypeFilter: [MarketProjectFilterType.Ideas],
                pattern: null,
            });
        }
    }, [publicStatisticsState?.data]);
    // if the user is logged in and creates a new project, the marketplace-projects must be refreshed to show it to him
    useEffect(() => {
        if (!projectsState.loaded && currentUser) {
            dispatchLoadMarketProjectsAction(marketSearchState);
        }
    }, [currentUser?.createdProjects]);
    // (re)load market content when marketSearchState changes
    useEffect(() => {
        dispatchLoadMarketProjectsAction(marketSearchState);
    }, [JSON.stringify(marketSearchState)]);
    // #endregion
    return <BaseLayout pageTitle={t("page.projects.index.title")} isMarketplace={true}>
    <Row className="justify-content-md-center">
      <Col className="text-center">
        <MarketFilterTabs chosenItem={MarketTabItems.ProjectsChosen}/>
        <MarketSearchForm categories={categories} sdgs={sdgs} searchParams={marketSearchState} initialCategoryId={initialCategoryId} initialSDGId={initialSDGId} publicStatistics={publicStatisticsState?.data}/>
        <PageError error={categoryRequest?.loadingError}/>
        <PageError error={sdgRequest?.loadingError}/>
        <PageError error={projectsState?.loadingError}/>
        <PageError error={publicStatisticsState?.loadingError}/>
      </Col>
    </Row>
    <Row>
      <Col>
        {// show spinner before the project list, to make a visual response when the user changes the filter criteria
        // when a list of project from previous search is visible
        (projectsState?.isLoading || categoryRequest?.isLoading || publicStatisticsState?.isLoading || sdgRequest?.isLoading) && <Spinner />}
      </Col>
    </Row>
    {projectsState?.loaded &&
            <ProjectScroller projects={projectsState.getItems<IProject>().filter(p => p.state === ProjectState.Active && !p.locked)} loadNext={() => dispatch(projectsState.getLoadNextPageAction())} hasNext={!!projectsState.nextLink} infoCard={marketSearchState.projectTypeFilter.includes(MarketProjectFilterType.Ideas) ? MarketInfoCardType.IdeaCard : MarketInfoCardType.ProjectCard}/>}
    {projectsState?.isLoading && projectsState?.itemIds.length > 3 &&
            // show spinner after the project list, to make sure, the user gets a visual response about the loading,
            // when scrolling even on longer lists
            <Row>
        <Col>
          {projectsState?.isLoading && <Spinner />}
        </Col>
      </Row>}
  </BaseLayout>;
};
const __Next_Translate__Page__191dbbefb7f__ = withAuthOnLoggedInOnlySetting(MarketPage);

    export default __appWithI18n(__Next_Translate__Page__191dbbefb7f__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: false,
      loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
    });
  